import React, { useState } from 'react';
import { COLUMN_TYPES } from './column-types';
import { cn } from '../../../lib/utils';
import { ArrowLeft, ArrowRight, Plus, Trash2 } from 'lucide-react';
import { Dropdown, DropdownContent, DropdownHeader, DropdownItem } from '../../../components/ui';
import { useViewport } from '../../../contexts/ViewportContext';

interface ColumnTypeMenuProps {
  columnId: string;
  currentType: string;
  onSelect: (type: string) => void;
  onClose: () => void;
  // Added props for column movement
  columnIndex?: number;
  columnsCount?: number;
  onAddColumnAfter?: (columnId: string) => void;
  // Position properties
  targetRef?: React.RefObject<HTMLElement>;
}

export function ColumnTypeMenu({
  columnId,
  currentType,
  onSelect,
  onClose,
  columnIndex = 0,
  columnsCount = 0,
  onAddColumnAfter,
  targetRef,
}: ColumnTypeMenuProps) {
  const { isMobile } = useViewport();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Empty trigger since the button is handled in the parent component
  const emptyTrigger = <span style={{ display: 'none' }} />;

  return (
    <Dropdown
      trigger={emptyTrigger}
      open={true}
      onOpenChange={open => {
        if (!open) onClose();
      }}
      width={220}
      isMobile={isMobile}
      position="bottom-right"
      showBackdrop={isMobile}
      mobilePosition={0.2}
      anchorElement={targetRef?.current || null}
      contentClassName="bg-white shadow-lg rounded border-0"
    >
      <DropdownContent>
        <DropdownHeader className="text-xs font-semibold text-gray-500 py-2">
          Column Type
        </DropdownHeader>

        <div className="overflow-y-auto border-b border-gray-100">
          {Object.entries(COLUMN_TYPES).map(([type, { label, icon }]) => (
            <DropdownItem
              key={type}
              leftIcon={icon}
              className={cn(
                'hover:bg-[var(--content-bg-color)]',
                currentType === type && 'bg-blue-50 text-blue-600'
              )}
              onClick={() => onSelect(type)}
            >
              {label}
            </DropdownItem>
          ))}
        </div>

        <DropdownHeader className="text-xs font-semibold text-gray-500 py-2">
          Options
        </DropdownHeader>

        {/* Move left button - disabled if first column */}
        <DropdownItem
          leftIcon={<ArrowLeft size={14} />}
          className={
            columnIndex > 0
              ? 'text-gray-700 hover:bg-[var(--content-bg-color)]'
              : 'text-gray-400 cursor-not-allowed'
          }
          onClick={() => {
            if (columnIndex > 0) {
              document.dispatchEvent(
                new CustomEvent('move-column', {
                  detail: { columnId, direction: 'left' },
                })
              );
              onClose();
            }
          }}
          disabled={columnIndex === 0}
        >
          Move Left
        </DropdownItem>

        {/* Move right button - disabled if last column */}
        <DropdownItem
          leftIcon={<ArrowRight size={14} />}
          className={
            columnIndex < columnsCount - 1
              ? 'text-gray-700 hover:bg-[var(--content-bg-color)]'
              : 'text-gray-400 cursor-not-allowed'
          }
          onClick={() => {
            if (columnIndex < columnsCount - 1) {
              document.dispatchEvent(
                new CustomEvent('move-column', {
                  detail: { columnId, direction: 'right' },
                })
              );
              onClose();
            }
          }}
          disabled={columnIndex === columnsCount - 1}
        >
          Move Right
        </DropdownItem>

        {/* Add column button */}
        {onAddColumnAfter && (
          <DropdownItem
            leftIcon={<Plus size={14} className="text-blue-500" />}
            className="hover:bg-[var(--content-bg-color)]"
            onClick={() => {
              onAddColumnAfter(columnId);
              onClose();
            }}
          >
            Add Column
          </DropdownItem>
        )}

        {!showDeleteConfirm ? (
          <DropdownItem
            leftIcon={<Trash2 size={14} className="text-red-500" />}
            className="text-red-500 hover:bg-[var(--content-bg-color)]"
            onClick={() => setShowDeleteConfirm(true)}
          >
            Delete Column
          </DropdownItem>
        ) : (
          <div className="w-full py-2 px-3">
            <p className="text-xs text-gray-700 mb-2">
              Are you sure you want to delete this column?
            </p>
            <div className="flex space-x-2">
              <button
                className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                onClick={() => {
                  document.dispatchEvent(
                    new CustomEvent('delete-column', {
                      detail: { columnId },
                    })
                  );
                  onClose();
                }}
              >
                Delete
              </button>
              <button
                className="px-2 py-1 text-xs bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </DropdownContent>
    </Dropdown>
  );
}
